/* eslint-disable @typescript-eslint/no-unused-vars */
import type { ReactElement } from 'react';

import { memo, useCallback, useId, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { SideNavItemTitle } from './SideNavItemTitle';
import { SideNavLink } from './SideNavLink';
import { SideNavItemSubMenuAnimation } from './SideNavSubMenuAnimation';
import { media } from '../../core/styles';
import type { NavElement, OneOf } from '~/types/topLevelNavigationEvents';
import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import type { NavigationItem } from '~/utilities/graphql/codegen';
import { SiteType } from '~/utilities/graphql/codegen';
import { getClassNames, getSiteStyles, getStyleObject } from '~/utilities/helpers';
import { getNavItemPath } from '~/utilities/helpers/getNavItemPath';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';
import { removeTagsFromHTML } from '~/utilities/parsers';

export type SideNavElement = OneOf<NavElement, 'nav_item' | 'account'>;

export interface SideNavItemProps extends NavigationItem {
  activeIndexArray?: string[];
  activeItemUrl?: string | null;
  currentIndex: string;
  depthLevel: number;
  isLastBranch?: boolean;
  /** Plain-text labels of the ancestors, used for the `nav_item_path` tracking parameter. */
  labelPath: string[];
  navElement: SideNavElement;
  parentActive?: boolean;
  parentLabel?: string;
  updateActiveItem: (arr: string[]) => void;
}

// TODO: Set correct color for sideNav-link--sale and .sideNav__leaf
const S = {
  SideNavItem: styled.li`
    ${({ theme }) => css`
      &[data-root='true'] {
        transform: translateZ(0);
        border-bottom: ${theme.borders.width.xs} solid ${theme.colors.border.subtle};
        box-sizing: border-box;
        display: block;
      }

      &[data-last-branch='true'] {
        padding-bottom: ${theme.padding.xs} 0;
      }

      height: max-content;

      & .sideNav__leaf:has(.sideNav-link--marginTop) {
        margin-top: ${theme.padding.sm};
      }

      & .sideNav-link--sale,
      .sideNav-link--sale:visited,
      .sideNav-link--sale:hover,
      .sideNav-link--sale:focus {
        color: red;
      }

      & .sideNav-link--highlight {
        font-weight: ${theme.variables.font.weight.bold};
      }

      & .sideNav-link--newArrivals,
      .sideNav-link--newArrivals:visited,
      .sideNav-link--newArrivals:hover,
      .sideNav-link--newArrivals:focus {
        color: ${theme.colors.content['brand-accent']};
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          padding: 0;

          &[data-root='true'] {
            padding: ${theme.padding.xs} 0;
          }

          margin-top: ${theme.padding['2xs']};

          & .sideNav__leaf {
            display: flex;
            align-items: center;
          }

          @media ${media(theme).greaterThan('sm')} {
            &[data-hidden='true'] {
              height: 0;
              visibility: hidden;
            }
          }
        `,
      })}
    `}
  `,
};

const SideNavItemComponent = ({
  activeIndexArray,
  activeItemUrl,
  children,
  currentIndex,
  depthLevel,
  hideForLoggedInVisitors,
  hideForNonLoggedInVisitors,
  isLastBranch,
  label,
  labelPath,
  navElement,
  parentActive = true,
  parentLabel,
  styleModifier,
  target,
  uid,
  updateActiveItem,
  url,
}: SideNavItemProps): ReactElement => {
  const accordionButtontId = useId();
  const accordionContentId = useId();

  const plainTextLabel = removeTagsFromHTML(label ?? '') ?? '';
  const itemLabelPath = [...labelPath, plainTextLabel];
  const branchChildCount = children?.filter(child => !child?.url).length ?? 0;

  const styleObject = useMemo(() => getStyleObject(styleModifier ?? ''), [styleModifier]);
  const classNames = useMemo(() => getClassNames(styleModifier ?? ''), [styleModifier]);
  const { loggedInRender } = useUserContext();
  const { track } = useTopNavTracking();

  const isExpanded = `${activeIndexArray?.slice(0, depthLevel + 1)}` === currentIndex;
  const shouldShow = !(
    label && (loggedInRender ? hideForLoggedInVisitors : hideForNonLoggedInVisitors)
  );
  const isActiveURL = activeItemUrl === url;
  // A string, so the handlers below stay stable for the memoized SideNavLink / SideNavItemTitle.
  const navItemPath = getNavItemPath(itemLabelPath);
  const navLevel = itemLabelPath.length;

  const handleLinkClick = useCallback(
    () =>
      track({
        event: 'nav_interaction',
        element: navElement,
        nav_item_label: plainTextLabel,
        nav_item_path: navItemPath,
        nav_level: navLevel,
        interaction_type: 'click',
      }),
    [navElement, navItemPath, navLevel, plainTextLabel, track]
  );

  const handleTitleClick = useCallback(
    () =>
      track({
        event: 'nav_interaction',
        element: navElement,
        nav_item_label: plainTextLabel,
        nav_item_path: navItemPath,
        nav_level: navLevel,
        interaction_type: isExpanded ? 'close' : 'open',
      }),
    [isExpanded, navElement, navItemPath, navLevel, plainTextLabel, track]
  );

  if (!shouldShow) {
    return <></>;
  }

  return (
    <S.SideNavItem
      className={url ? 'sideNav__leaf' : 'sideNav__branch'}
      data-last-branch={isLastBranch}
      data-hidden={uid === 'staticMyAccount_navigationNode' && !loggedInRender}
      data-root={depthLevel === 0}
      data-testid={isActiveURL ? 'navigation-node-active' : undefined}
    >
      {url ? (
        <SideNavLink
          classNames={classNames}
          dataNavItem={parentLabel ? `${parentLabel} - ${plainTextLabel}` : plainTextLabel}
          isActiveURL={isActiveURL}
          label={label}
          parentActive={parentActive}
          styleObject={styleObject}
          target={target}
          url={url}
          onClick={handleLinkClick}
        />
      ) : (
        <SideNavItemTitle
          activeIndexArray={activeIndexArray}
          classNames={classNames}
          currentIndex={currentIndex}
          dataNavItem={plainTextLabel}
          depthLevel={depthLevel}
          isExpanded={isExpanded}
          label={label}
          parentActive={parentActive}
          styleObject={styleObject}
          uid={uid}
          updateActiveItem={updateActiveItem}
          aria-controls={accordionContentId}
          id={accordionButtontId}
          onClick={handleTitleClick}
        />
      )}
      {children?.length ? (
        <SideNavItemSubMenuAnimation
          activeIndexArray={activeIndexArray}
          activeItemUrl={activeItemUrl}
          branchChildCount={branchChildCount}
          parentLabel={!url ? plainTextLabel : parentLabel}
          currentIndex={currentIndex}
          depthLevel={depthLevel}
          isExpanded={isExpanded}
          labelPath={itemLabelPath}
          navElement={navElement}
          updateActiveItem={updateActiveItem}
          id={accordionContentId}
          aria-labelledby={accordionButtontId}
        >
          {children}
        </SideNavItemSubMenuAnimation>
      ) : null}
    </S.SideNavItem>
  );
};

const arePropsEqual = (prevProps: SideNavItemProps, nextProps: SideNavItemProps): boolean => {
  const prevIsExpanded =
    `${prevProps.activeIndexArray?.slice(0, prevProps.depthLevel + 1)}` === prevProps.currentIndex;
  const nextIsExpanded =
    `${nextProps.activeIndexArray?.slice(0, nextProps.depthLevel + 1)}` === nextProps.currentIndex;
  const hasChildren = (nextProps.children?.length ?? 0) > 0;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { activeIndexArray: prevActiveIndexArray, ...prevRest } = prevProps;
  const { activeIndexArray: nextActiveIndexArray, ...nextRest } = nextProps;

  if (prevIsExpanded !== nextIsExpanded) {
    return false;
  }

  if (
    nextIsExpanded &&
    hasChildren &&
    !isEqual(prevProps.activeIndexArray, nextProps.activeIndexArray)
  ) {
    return false;
  }

  return isEqual(prevRest, nextRest);
};

export const SideNavItem = memo(SideNavItemComponent, arePropsEqual);
